import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import {
  PMCountCategoriesCard,
  PMDonutChartCard,
  PMSmallNumber,
  PMSmallNumberCardEditSchema,
} from "@pimo/pimo-components/src/lib/pm-components";

import { TestAppState } from "../../../app";
import { APP_ROUTES } from "../../../constants";
import { getDropdownCount } from "../../../utils/dropdown-options";
import { getLocalizedCardCopy } from "../../../utils/get-localized-card-copy";
import {
  getPluralOfProjectName,
  getProjectName,
} from "../../../utils/get-plural-of-project-name";
import { getOverviewCardCopy } from "../get-card-copy";

export function addSemaphoreStatusAndProjectCompletionCountCards(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
): void {
  const { language, itemName, itemNamePlural, getCardCopy } =
    getOverviewCardCopy(app);

  const semaphoreStatusOfProjectsDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "statusOfProjectsDonutChartComponent",
      title: "",
      description: "",
      labels: ["donutChart", "chart", "status"],
    },
  });
  semaphoreStatusOfProjectsDonutChartComponent.mapLayoutProps(
    ({ language }) => {
      return {
        componentId: "statusOfProjectsDonutChartComponent",
        labels: ["donutChart", "chart", "semaphoreStatus"],
        title: getCardCopy(language, "statusOfProjects").title,
        description: getCardCopy(language, "statusOfProjects").description,
      };
    }
  );

  semaphoreStatusOfProjectsDonutChartComponent.mapState(
    ({ dashboard, settings, language }) => {
      const statusCounts = dashboard?._calculated?.semaphoreStatusCounts ?? {};
      const data =
        [
          settings?.configuration?.semaphoreStatusDefinition?.[0].low,
          settings?.configuration?.semaphoreStatusDefinition?.[0].medium,
          settings?.configuration?.semaphoreStatusDefinition?.[0].high,
        ]?.map((entry) => {
          return {
            label: entry?.name ?? "",
            value: (entry && getDropdownCount(statusCounts, entry)) ?? 0,
            color: entry?.color ?? "",
          };
        }) ?? [];

      const handleStatusFilter = (status?: string) => {
        app?.patchAppState({
          filterData: {
            searchQueryFilter: [],
            statusFilter: status ? [status] : [],
            completionFilter: [],
            categoryFilter: [],
            updateStatusFilter: [],
          },
        });
        app?.navigate(APP_ROUTES.overview);
      };

      return {
        title: getLocalizedCardCopy(language, "statusOfProjects", {
          itemName,
          itemNamePlural,
          projectName: getProjectName(
            settings?.configuration?.projectName,
            language
          ),
          projectNamePlural: getPluralOfProjectName(
            settings?.configuration?.projectName,
            language
          ),
        }).title,
        icon: "DonutLarge",
        data,
        onCardClick: () => handleStatusFilter(),
        onSegmentClick: (segmentIndex: number) =>
          handleStatusFilter(data[segmentIndex]?.label),
      };
    }
  );

  semaphoreStatusOfProjectsDonutChartComponent.mapVisibility(({ settings }) => {
    return (
      (settings?.configuration?.statusReportingLevel ?? "overall") ===
      "category"
    );
  });

  const countCompletionOfProjectsComponent = view.addComponent({
    component: PMCountCategoriesCard,
    layoutProps: {
      componentId: "countCompletionOfProjectsComponent",
      labels: ["completion", "count"],
      description: getCardCopy(language, "projectCompletionCounts").description,
      title: getCardCopy(language, "projectCompletionCounts").title,
    },
  });

  countCompletionOfProjectsComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "countCompletionOfProjectsComponent",
      labels: ["completion", "count"],
      title: getCardCopy(language, "projectCompletionCounts").title,
      description: getCardCopy(language, "projectCompletionCounts").description,
    };
  });

  countCompletionOfProjectsComponent.mapState((appState) => {
    const projectDegreeOfCompletionCounts =
      appState.dashboard?._calculated?.projectDegreeOfCompletionCounts ?? {};
    const data =
      appState.settings?.configuration?.degreeOfCompletionDefinition?.map(
        (entry) => {
          return {
            label: entry.name ?? "",
            count: getDropdownCount(projectDegreeOfCompletionCounts, entry),
            color: entry.color ?? "",
          };
        }
      ) ?? [];

    const handleCompletionFilter = (completion?: string) => {
      app?.patchAppState({
        filterData: {
          searchQueryFilter: [],
          statusFilter: [],
          completionFilter: completion ? [completion] : [],
          categoryFilter: [],
          updateStatusFilter: [],
        },
      });
      app?.navigate(APP_ROUTES.overview);
    };

    return {
      title: getLocalizedCardCopy(
        appState.language || "en",
        "projectCompletionCounts",
        {
          itemName,
          itemNamePlural,
          projectName: getProjectName(
            appState.settings?.configuration?.projectName,
            appState.language || "en"
          ),
          projectNamePlural: getPluralOfProjectName(
            appState.settings?.configuration?.projectName,
            appState.language || "en"
          ),
        }
      ).title,
      icon: "DonutLarge",
      data,
      onCardClick: () => handleCompletionFilter(),
      onSegmentClick: (segmentIndex: number) =>
        handleCompletionFilter(data[segmentIndex]?.label),
    };
  });

  const ratioOfCompletedProjectsComponent = view.addComponent({
    component: PMSmallNumber,
    layoutProps: {
      componentId: "ratioOfCompletedProjectsComponent",
      labels: ["#ofProjects"],
      description: "",
      title: "",
      editSchema: PMSmallNumberCardEditSchema,
    },
  });

  ratioOfCompletedProjectsComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "ratioOfCompletedProjectsComponent",
      labels: ["#ofProjects"],
      description: getCardCopy(language, "numberOfCompletedProjects")
        .description,
      title: getCardCopy(language, "projectCompletionCounts").title,
      editSchema: PMSmallNumberCardEditSchema,
    };
  });

  ratioOfCompletedProjectsComponent.mapState(({ dashboard, ...appState }) => {
    return {
      showInfoIcon: false,
      showMenuIcon: false,
      title: getLocalizedCardCopy(
        appState.language || "en",
        "projectCompletionCounts",
        {
          itemName,
          itemNamePlural,
          projectName: getProjectName(
            appState.settings?.configuration?.projectName,
            appState.language || "en"
          ),
          projectNamePlural: getPluralOfProjectName(
            appState.settings?.configuration?.projectName,
            appState.language || "en"
          ),
        }
      ).title,
      number: `${dashboard?._calculated?.numberOfClosedProjects ?? 0}/${
        dashboard?.projects?.length ?? 0
      }`,

      description: getLocalizedCardCopy(
        appState.language || "en",
        "numberOfCompletedProjects",
        {
          itemName,
          itemNamePlural,
          projectName: getProjectName(
            appState.settings?.configuration?.projectName,
            appState.language || "en"
          ),
          projectNamePlural: getPluralOfProjectName(
            appState.settings?.configuration?.projectName,
            appState.language || "en"
          ),
        }
      ).description,
    };
  });
}
